import { speciaBehiver, specialCheck, specialCheckCurrent } from '@/define/limit'
import { GetMagnetKeyInfo, MagnetDynamicReport, PostMagnetSetKeyInfo } from './magnet_set'
import { DataDeadZone, PostMagnetSetKeyDeadZone } from '@libs/types/response'
import { KK } from '@/define/define'
import { SetMagnetSenior } from '../response'

// 发送的数据进行转换
// 注意：必须返回新对象，避免污染调用方持有的同一引用（例如 mgx_set.vue 的 changelist 还要复用原始未缩放的值）。
export const convertTravelToSend_Above6800 = (info: PostMagnetSetKeyInfo): PostMagnetSetKeyInfo => {
  if (!specialCheckCurrent(speciaBehiver.DeviceTravelAbove6800)) return info
  return {
    ...info,
    PressNum: info.PressNum > 0 ? (info.PressNum * 2) / 10 : info.PressNum,
    ReleaseNum: info.ReleaseNum > 0 ? (info.ReleaseNum * 2) / 10 : info.ReleaseNum,
    QuickTriggerPressNum: info.QuickTriggerPressNum > 0 ? (info.QuickTriggerPressNum * 2) / 10 : info.QuickTriggerPressNum,
    QuickTriggerReleaseNum: info.QuickTriggerReleaseNum > 0 ? (info.QuickTriggerReleaseNum * 2) / 10 : info.QuickTriggerReleaseNum,
  }
}
// 获取回来的数据进行转换
export const convertTravelFromReceive_Above6800 = (data: GetMagnetKeyInfo[]): GetMagnetKeyInfo[] => {
  if (!specialCheckCurrent(speciaBehiver.DeviceTravelAbove6800)) return data
  return data.map((item) => ({
    ...item,
    PressNum: item.PressNum > 0 ? (item.PressNum / 2) * 10 : item.PressNum,
    ReleaseNum: item.ReleaseNum > 0 ? (item.ReleaseNum / 2) * 10 : item.ReleaseNum,
    QuickTriggerPressNum: item.QuickTriggerPressNum > 0 ? (item.QuickTriggerPressNum / 2) * 10 : item.QuickTriggerPressNum,
    QuickTriggerReleaseNum: item.QuickTriggerReleaseNum > 0 ? (item.QuickTriggerReleaseNum / 2) * 10 : item.QuickTriggerReleaseNum,
  }))
}
// 发送的死区数据进行转换
export const convertDeadZoneToSend_Above6800 = (setData: PostMagnetSetKeyDeadZone): PostMagnetSetKeyDeadZone => {
  if (!specialCheckCurrent(speciaBehiver.DeviceTravelAbove6800)) return setData
  return {
    ...setData,
    Top: (setData.Top * 2) / 10,
    Bottom: (setData.Bottom * 2) / 10,
  }
}
// 获取回来的死区数据进行转换
export const convertDeadZoneFromReceive_Above6800 = (data: { [key: KK]: DataDeadZone }): { [key: KK]: DataDeadZone } => {
  if (!specialCheckCurrent(speciaBehiver.DeviceTravelAbove6800)) return data
  const result: { [key: KK]: DataDeadZone } = {}
  for (const key in data) {
    const item = data[key]
    result[key] = {
      ...item,
      Top: (item.Top / 2) * 10,
      Bottom: (item.Bottom / 2) * 10,
    }
  }
  return result
}
// 校准时行程转换
export const convertTravelForCalibration = (res: MagnetDynamicReport[]): MagnetDynamicReport[] => {
  if (!specialCheckCurrent(speciaBehiver.DeviceTravelAbove6800)) return res
  return res.map((item) => ({
    ...item,
    Value: item.Value > 0 ? item.Value * 10 : item.Value,
  }))
}
// DKS等高级改键发送的行程数据进行转换
export const convertSeniorTravelToSend_Above6800 = (value: SetMagnetSenior): SetMagnetSenior => {
  if (!specialCheckCurrent(speciaBehiver.DeviceTravelAbove6800)) return value
  return {
    ...value,
    Keys: value.Keys.map((item) => ({
      ...item,
      Travel: item.Travel > 0 ? item.Travel * 2 : item.Travel,
    })),
  }
}
// DKS等高级改键获取回来的行程数据进行转换
export const convertSeniorTravelFromReceive_Above6800 = (magenet: { [index: KK]: SetMagnetSenior }): { [index: KK]: SetMagnetSenior } => {
  if (!specialCheckCurrent(speciaBehiver.DeviceTravelAbove6800)) return magenet
  const result: { [index: KK]: SetMagnetSenior } = {}
  for (const key in magenet) {
    const item = magenet[key]
    result[key] = {
      ...item,
      Keys: item.Keys.map((k) => ({
        ...k,
        Travel: k.Travel > 0 ? k.Travel / 2 : k.Travel,
      })),
    }
  }
  return result
}
// 导入配置时行程转换
export const importConfigTravelClampValue = (value: number, min: number, max: number): number => {
  // 配置快照保留协议侧原始值。Above6800 设备应换算合法范围，而不是再次缩放值本身；
  // 否则普通行程会被重复缩放，或 0.005 mm 的 RT 原始值 1 被业务层最小值 5 抬高。
  const scale = specialCheckCurrent(speciaBehiver.DeviceTravelAbove6800) ? 2 / 10 : 1
  return Math.max(min * scale, Math.min(value, max * scale))
}

export interface ImportedTravelDeviceEncoding {
  pid: number
  unit: number
}

export interface ImportedTravelBoundary {
  min: number
  max: number
  step: number
}

const getProfileTravelScale = (pid: number): number => (specialCheck(speciaBehiver.DeviceTravelAbove6800, pid) ? 2 / 10 : 1)

export const importedTravelValueInMM = (value: number, device: ImportedTravelDeviceEncoding): number => value / (device.unit * getProfileTravelScale(device.pid))

/**
 * 配置快照保存协议侧整数，不同设备的同一个整数可能代表不同的毫米值。
 * 跨设备导入时先还原来源毫米值，再编码成目标设备整数并按目标精度取整。
 */
export const convertImportedTravelValue = (value: number, source: ImportedTravelDeviceEncoding, target: ImportedTravelDeviceEncoding, boundary: ImportedTravelBoundary): number => {
  const targetScale = getProfileTravelScale(target.pid)
  const targetUnitsPerMM = target.unit * targetScale
  const targetStep = Math.max(1, Math.round(boundary.step * targetScale))
  const targetMin = Math.round(boundary.min * targetScale)
  const targetMax = Math.round(boundary.max * targetScale)

  const converted = importedTravelValueInMM(value, source) * targetUnitsPerMM
  const aligned = Math.round(converted / targetStep) * targetStep
  return Math.max(targetMin, Math.min(aligned, targetMax))
}

const getSeniorProfileTravelScale = (pid: number): number => (specialCheck(speciaBehiver.DeviceTravelAbove6800, pid) ? 2 : 1)

const alignImportedValue = (value: number, min: number, max: number, step: number, anchor = min): number => {
  const safeStep = step > 0 ? step : 1
  const aligned = anchor + Math.round((value - anchor) / safeStep) * safeStep
  return Number(Math.max(min, Math.min(aligned, max)).toFixed(10))
}

export const importedDKSTravelValue = (value: number, pid: number): number => value / getSeniorProfileTravelScale(pid)

export const convertImportedDKSTravelValue = (value: number, sourcePid: number, targetPid: number, boundary: ImportedTravelBoundary): number => {
  const targetScale = getSeniorProfileTravelScale(targetPid)
  return alignImportedValue(importedDKSTravelValue(value, sourcePid) * targetScale, boundary.min * targetScale, boundary.max * targetScale, boundary.step * targetScale, 0)
}

export const importedMPTTravelValueInMM = (value: number, device: ImportedTravelDeviceEncoding): number => importedDKSTravelValue(value, device.pid) / device.unit

export const convertImportedMPTTravelValue = (value: number, source: ImportedTravelDeviceEncoding, target: ImportedTravelDeviceEncoding, boundary: ImportedTravelBoundary): number => {
  const targetScale = getSeniorProfileTravelScale(target.pid)
  const converted = importedMPTTravelValueInMM(value, source) * target.unit * targetScale
  return alignImportedValue(converted, boundary.min * targetScale, boundary.max * targetScale, boundary.step * targetScale)
}
